import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { useTheme } from "@/context/ThemeContext";
import { AdoptionSummary } from "../../api/getAdoptionSummary";

interface WeeklyConnectionsCardProps {
  summary: AdoptionSummary | null;
  loading: boolean;
}

const formatWeekStart = (value: string): string => {
  const date = new Date(`${value}T00:00:00Z`);
  if (isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const WeeklyConnectionsCard: React.FC<WeeklyConnectionsCardProps> = ({
  summary,
  loading,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const chartData = (summary?.weeklyConnections ?? []).map((week) => ({
    ...week,
    label: formatWeekStart(week.weekStart),
  }));

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
      <h2 className="text-base font-normal text-gray-900 dark:text-white">
        New Connections per Week
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        First-time connections over the last 12 weeks
      </p>

      {loading && !summary ? (
        <div className="h-64 flex items-center justify-center">
          <LoadingIndicator isFullScreen={false} />
        </div>
      ) : (
        <ResponsiveContainer height={256} width="100%">
          <BarChart
            data={chartData}
            margin={{ top: 16, right: 8, left: -16, bottom: 0 }}
          >
            <CartesianGrid
              stroke={isDark ? "#374151" : "#e5e7eb"}
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              interval={0}
              stroke={isDark ? "#9ca3af" : "#4b5563"}
              tick={{ fontSize: 11 }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              stroke={isDark ? "#9ca3af" : "#4b5563"}
              tick={{ fontSize: 12 }}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: isDark ? "#1f2937" : "#ffffff",
                border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                borderRadius: "0.375rem",
                color: isDark ? "#f3f4f6" : "#1f2937",
                fontSize: "0.875rem",
              }}
              cursor={{ fill: isDark ? "#33415555" : "#f1f5f955" }}
            />
            <Bar
              dataKey="count"
              fill={isDark ? "#94a3b8" : "#64748b"}
              name="New connections"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default WeeklyConnectionsCard;
